export const LEGAL_DOCUMENT_KEYS = [
  'terms',
  'privacy',
  'personal-information',
  'third-parties',
  'ai-content',
  'cancellation',
  'complaints',
] as const;

export type LegalDocumentKey = (typeof LEGAL_DOCUMENT_KEYS)[number];
export type RequiredLegalDocumentKey = 'terms' | 'privacy';

export interface LegalSection {
  id: string;
  title: string;
  paragraphs?: string[];
  items?: string[];
}

export interface LegalDocumentDefinition {
  key: LegalDocumentKey;
  title: string;
  shortTitle: string;
  summary: string;
  icon: string;
  version: string;
  updatedAt: string;
  effectiveAt: string | null;
  status: 'draft' | 'effective';
  requiresAcceptance: boolean;
  sections: LegalSection[];
}

export const LEGAL_OPERATOR = {
  name: '南京知鱼品牌顾问有限公司',
  address: '江苏省南京市江宁区天印大道1406号书香名苑商铺3幢104室',
  privacyEmail: 'sansel@zhiyubrand.com',
  supportEmail: 'sansel@zhiyubrand.com',
} as const;

const draftMeta = {
  version: '2026-09-18-draft.1',
  updatedAt: '2026-09-18',
  effectiveAt: null,
  status: 'draft' as const,
};

const effectiveMeta = {
  version: '2026-09-29-v1.0',
  updatedAt: '2026-09-29',
  effectiveAt: '2026-09-29',
  status: 'effective' as const,
};

export const LEGAL_DOCUMENTS: Record<LegalDocumentKey, LegalDocumentDefinition> = {
  terms: {
    key: 'terms',
    title: '灵犀工坊用户服务协议',
    shortTitle: '用户服务协议',
    summary: '说明账号、AI 协作、Workspace、内容发布与平台服务的使用边界。',
    icon: 'ri-file-list-3-line',
    ...effectiveMeta,
    requiresAcceptance: true,
    sections: [
      {
        id: 'intro',
        title: '一、协议范围与接受',
        paragraphs: [
          `本协议由你与${LEGAL_OPERATOR.name}就使用“灵犀工坊”官网、Web 工作台及相关服务共同订立。`,
          '创建账号前，请完整阅读并理解本协议及《隐私政策》。勾选同意并完成注册，表示你同意受当时有效版本约束。',
        ],
      },
      {
        id: 'eligibility',
        title: '二、适用用户',
        paragraphs: [
          '当前版本面向具有完全民事行为能力的个人创造者和小型协作团队。未满十八周岁的用户应当在监护人阅读并同意相关协议后使用；产品暂不主动面向不满十四周岁未成年人提供服务。',
          '代表组织使用服务的，你确认已获得该组织的必要授权。',
        ],
      },
      {
        id: 'account',
        title: '三、账号与安全',
        items: [
          '你应提供真实、准确且可联系的信息，并妥善保管密码、验证码、登录设备和第三方登录账号。',
          '账号原则上仅限本人或获授权的组织成员使用，不得出售、出租、出借或以其他方式转让。',
          '发现账号被盗用、异常登录或权限配置错误时，应及时修改凭据、撤销会话并联系平台。',
        ],
      },
      {
        id: 'service',
        title: '四、服务内容与变化',
        paragraphs: [
          '灵犀工坊提供 Workspace、组件构建与运行、Agent 协作、文件与知识库、能力商店、连接器、创意生成及相关配套能力。具体可用功能以账号套餐、端类型、地区、配置和页面实际展示为准。',
          '平台可因安全、维护、监管或产品迭代调整服务，但会依法对影响用户权益的重大变化进行通知。',
        ],
      },
      {
        id: 'ai',
        title: '五、AI 服务特别说明',
        items: [
          'AI 输出基于概率生成，可能包含不准确、不完整、过时或不适合特定场景的信息，不构成医疗、法律、金融或其他专业意见。',
          '你应对输入材料拥有合法处理权限，并在使用、发布或执行输出前进行必要复核。',
          '涉及删除、付费、外发、扩大权限、本地电脑操作等高风险动作时，以产品实际提供的确认与授权机制为准。',
          '生成合成内容的显式和隐式标识、导出及发布责任适用《AI 服务与生成内容规则》。',
        ],
      },
      {
        id: 'content',
        title: '六、用户内容与知识产权',
        paragraphs: [
          '你保留对合法上传、输入、创建或发布内容依法享有的权利。为提供存储、处理、预览、协作、发布和故障排查服务，你授予平台在服务范围内必要、非独占的处理许可。',
          '你不得上传侵犯知识产权、商业秘密、人格权、隐私权或其他合法权益的内容。平台不会因提供技术能力而取得你未明确授予的所有权。',
        ],
      },
      {
        id: 'workspace',
        title: '七、Workspace 与协作责任',
        items: [
          'Workspace 所有者和管理者负责成员邀请、角色、授权、组件安装和共享范围的配置。',
          '成员只能在被授权范围内访问和处理内容，不得绕过权限边界或擅自扩散组织数据。',
          '删除成员、实例、组件或 Workspace 可能影响协作内容；执行不可逆操作前应阅读页面影响提示。',
        ],
      },
      {
        id: 'marketplace',
        title: '八、能力商店与公开发布',
        paragraphs: [
          '发布者应保证组件、Skill、素材、描述和更新具有合法来源，准确披露所需权限、网络目标、数据类型和保留策略。安装者应在授权前审阅这些信息。',
          '平台可对涉嫌侵权、违法、欺诈、恶意代码或重大安全风险的内容采取限制展示、暂停安装、隔离运行或下架措施。',
        ],
      },
      {
        id: 'prohibited',
        title: '九、禁止行为',
        items: [
          '利用服务实施违法犯罪、诈骗、侵权、骚扰、歧视、传播违法和有害信息等行为。',
          '绕过身份、权限、沙箱、配额、安全检查、内容标识或审计机制。',
          '窃取凭据、攻击服务、恶意消耗资源、传播恶意代码或未经授权访问他人数据。',
          '将服务用于依法需要资质而你不具备相应资质的高风险专业活动。',
        ],
      },
      {
        id: 'fees',
        title: '十、费用与订单',
        paragraphs: [
          '首发阶段暂未开通在线支付、自动续费、创作者结算和付费套餐购买。后续开通前，我们将另行公布适用的购买、退款、发票、自动续费和结算规则，并在需要时取得你的确认。',
          '未经用户明确确认，Agent 不得代替用户接受付费。',
        ],
      },
      {
        id: 'suspension',
        title: '十一、暂停、终止与责任边界',
        paragraphs: [
          '出现安全风险、违法违规、严重违约、监管要求或服务不可抗力时，平台可在必要范围内限制、暂停或终止相关功能，并在法律要求的范围内提供通知和申诉渠道。',
          '平台将采取合理措施保障服务安全和稳定，但不对不可控第三方服务、网络故障或用户配置导致的损失作绝对保证。依法不得排除或限制的责任不受本条影响。',
        ],
      },
      {
        id: 'contact',
        title: '十二、协议更新、联系与争议解决',
        paragraphs: [
          `涉及用户权利义务的重大变化将通过站内通知、页面提示或其他适当方式告知。客服与投诉邮箱：${LEGAL_OPERATOR.supportEmail}。`,
          `本协议的订立、效力、解释、履行及争议解决适用中华人民共和国法律。发生争议时，双方应先友好协商；协商不成的，可向有管辖权的人民法院提起诉讼。运营主体地址：${LEGAL_OPERATOR.address}。`,
        ],
      },
    ],
  },
  privacy: {
    key: 'privacy',
    title: '灵犀工坊隐私政策',
    shortTitle: '隐私政策',
    summary: '解释我们如何收集、使用、共享、保存和保护个人信息，以及你如何行使权利。',
    icon: 'ri-shield-user-line',
    ...effectiveMeta,
    requiresAcceptance: true,
    sections: [
      {
        id: 'overview',
        title: '一、政策说明',
        paragraphs: [
          `本政策适用于${LEGAL_OPERATOR.name}提供的灵犀工坊服务。我们遵循合法、正当、必要、诚信和最小范围原则处理个人信息。`,
          '摘要不能替代完整政策。你可通过账户内“隐私与数据”查看同意记录、提交查阅复制、删除、限制处理或账号注销等请求。',
        ],
      },
      {
        id: 'collection',
        title: '二、我们处理的信息',
        items: [
          '账号与身份：显示名称、邮箱、手机号、微信标识、验证码状态、登录方式和账号状态。',
          'Workspace 与协作：工作区、成员角色、邀请、页面、组件、Record、文档、任务、操作与审计记录。',
          'AI 协作：提示词、消息、上下文、任务、记忆、知识摘要、模型配置、生成结果和安全审核结果。',
          '文件与设备：上传文件、对象信息、设备类型、客户端版本、授权目录显示名，以及经单次授权读取的页面或屏幕信息。',
          '连接器与第三方登录：飞书、企业微信、钉钉和微信等授权标识、私聊内容、回调记录和加密凭据。',
          '交易与运营：订单、Credits、支付结果、创作者收款资料、营销来源、使用统计、IP/网络与安全日志。',
        ],
      },
      {
        id: 'purpose',
        title: '三、处理目的与方式',
        items: [
          '创建和维护账号、验证身份、恢复会话并保障账号安全。',
          '提供 Workspace、文件、组件、AI、连接器、协作、订单和客户支持功能。',
          '执行用户明确发起或授权的模型调用、本地电脑操作、外部连接和内容发布。',
          '计量额度、处理订单、预防欺诈、排查故障、履行审计及法律义务。',
          '在获得必要同意或具备其他合法处理依据的前提下进行产品分析和活动归因。',
        ],
      },
      {
        id: 'permissions',
        title: '四、设备权限与本地电脑能力',
        paragraphs: [
          '相机、相册、麦克风、通知、屏幕、页面、本地文件和目录等权限仅在相应功能需要时申请。拒绝非必要权限不会影响其他无关功能。',
          '桌面端本地电脑模式由用户主动选择只读、按需确认或完全访问；真实本机路径保存在本地安全存储，服务端仅保存设备、目录显示名、能力集合和审计所需记录。',
        ],
      },
      {
        id: 'ai-providers',
        title: '五、AI 模型与输入内容',
        paragraphs: [
          '为完成你发起的 AI 任务，必要的提示词、上下文、文件片段或结构化数据可能发送给你选择的平台模型或 BYOK 服务商。调用前应按页面所示确认供应商、数据范围和用途。',
          '我们不会仅因提供服务而宣称所有供应商均不使用数据训练；具体规则取决于实际供应商合同、账号类型和配置。涉及境外接收方时，将另行履行适用的告知、单独同意和数据出境义务。',
        ],
      },
      {
        id: 'sharing',
        title: '六、委托处理、共享与公开',
        paragraphs: [
          '我们可能委托云基础设施、短信邮件、模型、支付、安全与客服服务商处理实现功能所必要的信息，并通过合同约束其处理目的、范围和安全义务。',
          '向独立个人信息处理者提供信息、公开个人信息或发生法定需要单独同意的情形时，我们会另行告知并取得相应同意。实际接收方见《第三方信息共享清单》。',
        ],
      },
      {
        id: 'cross-border',
        title: '七、跨境处理',
        paragraphs: [
          '平台默认优先使用境内可用的基础设施和服务。你主动配置境外模型或其他境外服务时，相关数据可能发生跨境传输。',
          '首发阶段不提供由平台统一配置的境外系统模型。你自行配置境外模型或其他境外服务时，应先确认拥有向该服务提供数据的合法权限，并阅读其数据处理规则；我们会在平台主动提供跨境处理功能前，另行履行适用的告知、同意和评估义务。',
        ],
      },
      {
        id: 'retention',
        title: '八、保存期限',
        paragraphs: [
          '个人信息原则上仅在实现处理目的所需的最短期限内保存。账号、Workspace、文件、AI 会话、订单、审计与备份具有不同生命周期。',
          '账号资料、Workspace 内容、文件和 AI 协作内容在账号或对应内容仍由你保留期间持续保存；你删除内容、提交账号注销请求或服务终止后，我们将依照实际处理目的和适用法律进入删除、匿名化或必要留存流程。安全审计、争议处理和依法必须保存的信息在相应期限内保留。技术上不能立即删除的备份副本将停止除存储和必要安全保护之外的处理，并在备份轮换后删除或匿名化。',
        ],
      },
      {
        id: 'security',
        title: '九、安全保护',
        items: [
          '采用传输加密、访问控制、凭据加密、短期能力令牌、沙箱隔离、日志脱敏和备份等措施。',
          '日志默认不记录请求正文、完整模型上下文、Record 值、Token 和 Secret；调试采样需授权、脱敏并设置到期时间。',
          '发生可能影响个人权益的安全事件时，我们将依法采取补救措施并进行通知或报告。',
        ],
      },
      {
        id: 'rights',
        title: '十、你的权利',
        items: [
          '查阅、复制、更正、补充、删除或限制处理个人信息。',
          '撤回基于同意作出的授权，解绑登录方式、连接器或设备。',
          '在符合法定条件和技术可行时请求个人信息转移。',
          '申请注销账号，要求解释个人信息处理规则，或对处理决定提出投诉。',
        ],
      },
      {
        id: 'minors',
        title: '十一、未成年人保护',
        paragraphs: [
          '当前服务暂不主动面向不满十四周岁未成年人。如果发现未经监护人同意处理了该类信息，我们将核实并依法删除或采取其他必要措施。',
          '如未来面向不满十四周岁未成年人提供服务，我们将在上线前提供年龄识别、监护人同意和专门的未成年人个人信息处理规则。',
        ],
      },
      {
        id: 'cookies',
        title: '十二、Cookie 与本地存储',
        paragraphs: [
          '我们使用必要的 HttpOnly Cookie、本地安全存储或客户端缓存维持登录、保存主题和基础偏好。营销来源参数用于识别注册入口和评估活动效果。',
          '如未来启用非必要分析、广告或跨站跟踪工具，将更新清单并在适用场景提供选择机制。',
        ],
      },
      {
        id: 'updates',
        title: '十三、政策更新',
        paragraphs: [
          '处理目的、方式、信息种类或其他对你权益有重大影响的内容发生变化时，我们会以显著方式通知，并在法律要求的场景重新取得同意。历史版本将在法律中心保留。',
        ],
      },
      {
        id: 'privacy-contact',
        title: '十四、联系我们',
        paragraphs: [
          `隐私事务联系邮箱：${LEGAL_OPERATOR.privacyEmail}；联系地址：${LEGAL_OPERATOR.address}。你也可以登录后通过“隐私与数据”提交请求并获得请求编号。`,
          '我们将在收到你的请求后核验身份和请求范围，并在法律法规规定的期限内处理或说明理由。对个人信息处理有疑问、意见或投诉的，可通过上述邮箱联系；你也可以向有管辖权的个人信息保护、网信或其他监管部门投诉举报。',
        ],
      },
    ],
  },
  'personal-information': {
    key: 'personal-information',
    title: '个人信息收集清单',
    shortTitle: '个人信息清单',
    summary: '按业务场景列明处理的信息、目的、必要性和保存安排。',
    icon: 'ri-list-check-3',
    ...draftMeta,
    requiresAcceptance: false,
    sections: [
      {
        id: 'necessary',
        title: '一、核心功能必要信息',
        items: [
          '邮箱注册：邮箱、密码摘要、显示名称；用于创建账号、登录和找回访问能力。',
          '手机号/微信登录：手机号或平台 OpenID/UnionID、验证时间；用于身份验证与绑定账号。',
          '会话安全：会话标识、设备名称、客户端类型、User-Agent、IP 摘要、登录时间；用于登录态与风险控制。',
          'Workspace：成员、角色、页面、组件、任务、消息、Record、文件及版本；用于用户主动创建、运行和协作。',
        ],
      },
      {
        id: 'optional',
        title: '二、按需功能信息',
        items: [
          'AI 协作：提示词、消息、上下文、文件片段、记忆和生成结果；仅在用户发起模型任务时处理。',
          '企业连接：连接器身份、私聊文本、回调信息和加密凭据；仅在绑定对应平台后处理。',
          '本地电脑：设备能力、目录显示名、经授权的文件、页面、屏幕和命令结果；仅在桌面端主动启用后处理。',
          '创意中心：图片、视频、语音提示、参数、任务状态和生成产物；仅在用户启用并发起任务后处理。',
          '交易与结算：订单、支付结果、Credits、发票或创作者收款信息；仅在购买或结算时处理。',
        ],
      },
      {
        id: 'retention-table',
        title: '三、期限与删除',
        paragraphs: [
          '【待法务确认】账号信息、Workspace 内容、AI 会话、文件、订单、审计日志和备份的实际保存期限尚需由业务、运维与法务共同确认。正式发布前不得用模糊的“长期保存”替代具体期限或确定方法。',
        ],
      },
    ],
  },
  'third-parties': {
    key: 'third-parties',
    title: '第三方信息共享清单',
    shortTitle: '第三方共享清单',
    summary: '说明登录、模型、连接器、通信和支付场景中的外部接收方。',
    icon: 'ri-share-line',
    ...draftMeta,
    requiresAcceptance: false,
    sections: [
      {
        id: 'principles',
        title: '一、共享原则',
        paragraphs: [
          '仅在功能实际启用、具有明确目的和必要数据范围时向第三方提供信息。供应商、处理地点、合同角色或数据范围变化时，本清单应同步更新。',
        ],
      },
      {
        id: 'providers',
        title: '二、当前可能使用的服务',
        items: [
          '微信：扫码登录、小程序登录和订阅消息；可能处理 OpenID、UnionID、登录临时凭证和消息投递信息。',
          '飞书、企业微信、钉钉：用户主动绑定的企业连接；可能处理平台身份、私聊文本、事件回调和发送结果。',
          'DeepSeek、百炼、火山引擎及用户配置的 OpenAI-compatible 服务：完成文本、图片、视频或语音生成；处理范围取决于用户选择的模型和任务上下文。',
          '短信、邮件、支付及云基础设施供应商：【待运营配置后补充真实公司名称、产品名称、数据种类、目的、处理方式和隐私政策链接】。',
        ],
      },
      {
        id: 'processors',
        title: '三、供应商管理',
        paragraphs: [
          '正式启用供应商前，应完成安全和隐私评估，签署数据处理条款，限制转委托和留存，并验证用户权利请求能够传递到相关供应商。',
        ],
      },
    ],
  },
  'ai-content': {
    key: 'ai-content',
    title: 'AI 服务与生成内容规则',
    shortTitle: 'AI 与生成内容规则',
    summary: '说明模型输入输出、内容标识、使用责任和禁止场景。',
    icon: 'ri-sparkling-2-line',
    ...draftMeta,
    requiresAcceptance: false,
    sections: [
      {
        id: 'nature',
        title: '一、AI 服务性质',
        paragraphs: [
          '灵犀工坊帮助用户把需求转化为组件、内容和可执行操作，但 AI 不具备人的判断能力。输出可能出错，用户应根据使用场景进行复核。',
        ],
      },
      {
        id: 'input',
        title: '二、输入内容',
        items: [
          '不要输入无权处理的个人信息、商业秘密、国家秘密、账号密钥或受限制数据。',
          '上传或引用第三方内容前，应确认具备相应权利或合法处理依据。',
          '需要把敏感信息发送给模型时，应在页面明确授权，并优先使用脱敏或最小范围数据。',
        ],
      },
      {
        id: 'output',
        title: '三、输出使用',
        items: [
          '发布、交付、执行或据此作出重要决定前，应核验事实、权利来源、安全性和适用性。',
          '不得冒充自然人或组织，不得利用生成内容实施欺诈、侵权、骚扰、歧视或其他违法行为。',
          '医疗、法律、金融、安全生产等专业领域应由具备资质的人员最终判断。',
        ],
      },
      {
        id: 'labeling',
        title: '四、生成合成内容标识',
        paragraphs: [
          '平台会按照适用规则对文本、图片、音频、视频或虚拟场景添加显式标识，并在可导出文件的元数据中添加隐式标识。具体样式以生成结果和导出页面展示为准。',
          '用户发布生成合成内容时应主动声明，不得恶意删除、篡改、伪造或隐匿平台添加的标识。申请不带显式标识的内容时，仍应遵守页面说明的标识义务和使用责任。',
        ],
      },
      {
        id: 'models',
        title: '五、模型供应商与 BYOK',
        paragraphs: [
          '系统模型和 BYOK 模型可能具有不同的数据条款、地区、价格和可用性。选择供应商前，应阅读页面告知及该供应商规则。平台不会把 BYOK 理解为用户放弃隐私权。',
        ],
      },
      {
        id: 'enforcement',
        title: '六、安全处置与申诉',
        paragraphs: [
          '发现违法内容、重大安全风险或滥用时，平台可停止生成、阻断传输、限制功能、保留必要记录并依法报告。用户可以通过投诉举报渠道提出说明或申诉。',
        ],
      },
    ],
  },
  cancellation: {
    key: 'cancellation',
    title: '账号注销须知',
    shortTitle: '账号注销须知',
    summary: '说明注销申请、资产影响、法定留存和不可恢复事项。',
    icon: 'ri-user-unfollow-line',
    ...draftMeta,
    requiresAcceptance: false,
    sections: [
      {
        id: 'before',
        title: '一、提交前请确认',
        items: [
          '你已处理仍由你管理的 Workspace、成员、公开组件、进行中任务、订单、退款、结算和争议。',
          '你已下载需要保留的文件、数据、组件版本、AI 会话和其他成果。',
          '注销账号与退出登录不同，完成后原账号和相关身份可能无法恢复。',
        ],
      },
      {
        id: 'impact',
        title: '二、可能受到影响的数据',
        paragraphs: [
          '账号资料、个人 Workspace、私有文件、登录身份、设备授权和连接器授权将进入清理流程。公开发布内容、协作内容、交易记录、安全审计和法律要求保存的信息将根据权利归属、其他用户权益和法定义务分别处理。',
        ],
      },
      {
        id: 'request',
        title: '三、如何申请',
        paragraphs: [
          '登录后进入“账户设置 → 隐私与数据”，选择“账号注销”并说明需要特别处理的资产。系统会生成请求编号。完成必要的身份和资产核验后再执行注销。',
          '【待法务确认】正式上线前需明确核验方式、处理期限、撤回窗口和删除完成通知。',
        ],
      },
      {
        id: 'retention',
        title: '四、依法保留',
        paragraphs: [
          '法律法规要求继续保存，或删除在技术上暂时难以实现的信息，将停止除存储和必要安全保护之外的处理，并在条件满足后删除或匿名化。',
        ],
      },
    ],
  },
  complaints: {
    key: 'complaints',
    title: '投诉举报与侵权处理规则',
    shortTitle: '投诉与侵权处理',
    summary: '提供隐私、内容安全、知识产权和组件风险的反馈途径。',
    icon: 'ri-alarm-warning-line',
    ...draftMeta,
    requiresAcceptance: false,
    sections: [
      {
        id: 'scope',
        title: '一、受理范围',
        items: [
          '个人信息处理、账号安全、骚扰欺诈或其他用户权益问题。',
          '违法和有害信息、AI 生成内容标识、未成年人保护问题。',
          '著作权、商标权、专利权、商业秘密、肖像权和名誉权等侵权。',
          '组件、Skill、连接器或发布者存在恶意代码、越权访问或虚假披露。',
        ],
      },
      {
        id: 'materials',
        title: '二、建议提供的材料',
        items: [
          '请求人身份与联系方式，以及代表权利人提交时的授权证明。',
          '涉及的页面、组件、内容、账号、订单或请求编号。',
          '问题描述、权利证明、相关截图或其他可核验材料。请勿提交无关的敏感信息或明文密钥。',
        ],
      },
      {
        id: 'process',
        title: '三、处理流程',
        paragraphs: [
          '平台将核验材料并根据风险采取保存证据、限制传播、暂停安装、隔离运行、删除内容或恢复服务等必要措施。涉及其他用户时，会在保护隐私和商业秘密的前提下给予必要的陈述或申诉机会。',
          `投诉邮箱：${LEGAL_OPERATOR.supportEmail}。登录用户也可以通过“隐私与数据”提交请求。`,
        ],
      },
      {
        id: 'timing',
        title: '四、反馈时限',
        paragraphs: [
          '【待法务确认】正式发布前需明确不同类型请求的确认、补充材料、处置和申诉时限，并建立与后台工单对应的处理流程。',
        ],
      },
    ],
  },
};

export const LEGAL_DOCUMENT_LIST = LEGAL_DOCUMENT_KEYS.map((key) => LEGAL_DOCUMENTS[key]);

export const REQUIRED_LEGAL_ACCEPTANCE = {
  termsVersion: LEGAL_DOCUMENTS.terms.version,
  privacyVersion: LEGAL_DOCUMENTS.privacy.version,
} as const;

export interface LegalAcceptanceInput {
  accepted: true;
  termsVersion: string;
  privacyVersion: string;
}

export interface LegalAcceptanceView {
  id: string;
  documentKey: RequiredLegalDocumentKey;
  documentVersion: string;
  acceptedAt: string;
  clientType: 'web' | 'desktop' | 'mobile';
}

export interface LegalAcceptanceDetailView extends LegalAcceptanceView {
  contentHash: string;
  document: LegalDocumentDefinition;
}

export const PRIVACY_REQUEST_TYPES = [
  'access',
  'copy',
  'correct',
  'delete',
  'restrict',
  'withdraw-consent',
  'account-deletion',
] as const;

export type PrivacyRequestType = (typeof PRIVACY_REQUEST_TYPES)[number];
export type PrivacyRequestStatus =
  'pending' | 'in-progress' | 'completed' | 'rejected' | 'cancelled';

export interface PrivacyRequestView {
  id: string;
  type: PrivacyRequestType;
  details: string;
  status: PrivacyRequestStatus;
  submittedAt: string;
  updatedAt: string;
  resolutionNote?: string;
  resolvedAt?: string;
}

export interface PrivacyRequestAdminView extends PrivacyRequestView {
  userId: string;
  handledBy?: string;
}

export interface PrivacyCenterView {
  acceptances: LegalAcceptanceView[];
  requests: PrivacyRequestView[];
}
